<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex flex-wrap align-items-center text-white" style="padding: 32px 0; gap: 16px;">
            <div class="header-icon-wrap">
              <i :class="isDrug ? 'fe fe-package' : 'fe fe-activity'" style="font-size: 22px;"></i>
            </div>
            <div class="flex-grow-1" style="min-width: 220px;">
              <small class="text-uppercase font-weight-bold" style="opacity: .75; letter-spacing: .08em;">
                {{ isDrug ? 'Drug' : 'Service' }} usage
              </small>
              <h3 class="page-title text-white mb-2">{{ item ? item.name : (notFound ? 'Not found' : '…') }}</h3>
              <div v-if="item" class="d-flex flex-wrap" style="gap: 8px;">
                <span v-if="item.code" class="header-id-badge"><i class="fe fe-hash mr-1"></i> {{ item.code }}</span>
                <span v-if="item.category" class="header-id-badge"><i class="fe fe-tag mr-1"></i> {{ item.category }}</span>
                <span v-if="item.dosage" class="header-id-badge">{{ [item.dosage, item.strengths, item.presentation].filter(Boolean).join(' · ') }}</span>
                <span v-if="item.price !== null" class="header-id-badge">{{ money(item.price) }}</span>
                <span class="header-id-badge">{{ item.tariff === 'national' ? 'National (NHIA) tariff' : 'Agency tariff' }}</span>
                <span v-if="item.is_investigation" class="header-id-badge"><i class="fe fe-search mr-1"></i> Investigation</span>
                <span v-if="item.deleted" class="header-status header-status--inactive"><i class="fe fe-trash-2 mr-1"></i> Removed from the price list</span>
              </div>
            </div>
            <router-link :to="isDrug ? '/manage-drugs' : '/manage-services'" class="btn btn-outline-light btn-sm font-weight-bold">
              <i class="fe fe-arrow-left mr-1"></i> {{ isDrug ? 'All drugs' : 'All services' }}
            </router-link>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">
        <div class="card action-card shadow-sm mb-4">
          <div class="card-body py-3 px-4 d-flex flex-wrap align-items-center" style="gap: 10px;">
            <PeriodPicker v-model="period" with-granularity @change="load" />
            <small class="text-muted ml-auto">Encounters and claims recorded in your agency</small>
          </div>
        </div>

        <div v-if="notFound" class="card action-card shadow-sm">
          <div class="card-body text-center text-muted py-5">
            <i class="fe fe-alert-circle d-block mb-2" style="font-size: 2rem;"></i>
            {{ errorMessage || 'This item could not be found on your price list.' }}
          </div>
        </div>

        <div v-else-if="loading && !data" class="card action-card shadow-sm">
          <div class="card-body text-center text-muted py-5"><i class="mdi mdi-loading mdi-spin mr-1"></i> Loading usage…</div>
        </div>

        <template v-else-if="data">
          <!-- ── KPI tiles ── -->
          <div class="row stat-tiles">
            <div class="col-lg col-sm-6 mb-4" v-for="kpi in kpis" :key="kpi.label">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-4">
                  <p class="mb-0 text-muted" style="font-size: .72rem; text-transform: uppercase; letter-spacing: .04em;">{{ kpi.label }}</p>
                  <h4 class="mb-0 font-weight-bold text-dark">{{ kpi.value }}</h4>
                  <small class="text-muted">{{ kpi.hint }}</small>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Trend ── -->
          <div class="card table-card shadow-sm mb-4">
            <div class="d-flex flex-wrap align-items-center justify-content-between px-4 pt-4 pb-2" style="gap: 8px;">
              <h6 class="mb-0 font-weight-bold"><i class="fe fe-trending-up text-primary mr-2"></i> Over time</h6>
              <div class="btn-group btn-group-sm" role="group" aria-label="Measure">
                <button v-for="m in measures" :key="m.key" type="button" class="btn"
                  :class="measure === m.key ? 'btn-primary' : 'btn-outline-primary'" @click="measure = m.key">{{ m.label }}</button>
              </div>
            </div>
            <div class="px-3 pb-3">
              <apexchart v-if="data.kpis.uses" type="bar" height="300" :options="trendOptions" :series="trendSeries" :key="'t' + chartKey + measure" />
              <div v-else class="empty-note">No use recorded in this period.</div>
            </div>
          </div>

          <!-- ── Demography ── -->
          <div class="row">
            <div class="col-lg-4 mb-4">
              <div class="card table-card shadow-sm h-100">
                <div class="px-4 pt-4 pb-2"><h6 class="mb-0 font-weight-bold">Sex</h6><small class="text-muted">People who received it</small></div>
                <div class="px-3 pb-3">
                  <apexchart v-if="data.kpis.patients" type="donut" height="260" :options="genderOptions" :series="genderSeries" :key="'g' + chartKey" />
                  <div v-else class="empty-note">No patients in this period.</div>
                </div>
              </div>
            </div>
            <div class="col-lg-8 mb-4">
              <div class="card table-card shadow-sm h-100">
                <div class="px-4 pt-4 pb-2"><h6 class="mb-0 font-weight-bold">Age group and sex</h6><small class="text-muted">Age at first use in the period</small></div>
                <div class="px-3 pb-3">
                  <apexchart v-if="data.kpis.patients" type="bar" height="260" :options="ageOptions" :series="ageSeries" :key="'a' + chartKey" />
                  <div v-else class="empty-note">No patients in this period.</div>
                </div>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <div class="card table-card shadow-sm h-100">
                <div class="px-4 pt-4 pb-2"><h6 class="mb-0 font-weight-bold">Programme</h6><small class="text-muted">A dependant counts under the principal's programme</small></div>
                <div class="px-3 pb-3">
                  <apexchart v-if="data.demography.programme.length" type="bar" :height="barHeight(data.demography.programme)" :options="hbarOptions(data.demography.programme.map(r => r.key))"
                    :series="[{ name: 'People', data: data.demography.programme.map(r => r.count) }]" :key="'p' + chartKey" />
                  <div v-else class="empty-note">No patients in this period.</div>
                </div>
              </div>
            </div>
            <div class="col-lg-6 mb-4">
              <div class="card table-card shadow-sm h-100">
                <div class="px-4 pt-4 pb-2"><h6 class="mb-0 font-weight-bold">LGA</h6><small class="text-muted">Where the patient (or their principal) lives, top 15</small></div>
                <div class="px-3 pb-3">
                  <apexchart v-if="data.demography.lga.length" type="bar" :height="barHeight(data.demography.lga)" :options="hbarOptions(data.demography.lga.map(r => r.name))"
                    :series="[{ name: 'People', data: data.demography.lga.map(r => r.count) }]" :key="'l' + chartKey" />
                  <div v-else class="empty-note">No patients in this period.</div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <!-- ── Facilities ── -->
            <div class="col-lg-7 mb-4">
              <div class="card table-card shadow-sm h-100">
                <div class="px-4 pt-4 pb-3"><h6 class="mb-0 font-weight-bold"><i class="fe fe-home text-primary mr-2"></i> Top facilities</h6></div>
                <div class="table-responsive">
                  <table class="table mb-0">
                    <thead class="table-head">
                      <tr><th class="table-th px-4">Facility</th><th class="table-th text-right">Uses</th><th class="table-th text-right">Quantity</th><th class="table-th text-right">People</th><th class="table-th text-right pr-4">Claimed</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="f in data.facilities" :key="f.id" class="table-row">
                        <td class="px-4 py-2" style="font-size: .88rem;">{{ f.name }}</td>
                        <td class="py-2 text-right">{{ num(f.uses) }}</td>
                        <td class="py-2 text-right">{{ num(f.quantity) }}</td>
                        <td class="py-2 text-right">{{ num(f.patients) }}</td>
                        <td class="py-2 text-right pr-4">{{ money(f.amount_claimed) }}</td>
                      </tr>
                      <tr v-if="!data.facilities.length"><td colspan="5" class="text-center text-muted py-4">No use recorded in this period.</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <!-- ── Diagnoses ── -->
            <div class="col-lg-5 mb-4">
              <div class="card table-card shadow-sm h-100">
                <div class="px-4 pt-4 pb-3"><h6 class="mb-0 font-weight-bold"><i class="fe fe-clipboard text-primary mr-2"></i> Given for</h6><small class="text-muted">Diagnosis on the encounter or claim</small></div>
                <div class="table-responsive">
                  <table class="table mb-0">
                    <thead class="table-head"><tr><th class="table-th px-4">Diagnosis</th><th class="table-th text-right">Uses</th><th class="table-th text-right pr-4">People</th></tr></thead>
                    <tbody>
                      <tr v-for="d in data.diagnoses" :key="d.id" class="table-row">
                        <td class="px-4 py-2" style="font-size: .88rem;"><span v-if="d.code" class="text-muted mr-1">({{ d.code }})</span>{{ d.name }}</td>
                        <td class="py-2 text-right">{{ num(d.uses) }}</td>
                        <td class="py-2 text-right pr-4">{{ num(d.patients) }}</td>
                      </tr>
                      <tr v-if="!data.diagnoses.length"><td colspan="3" class="text-center text-muted py-4">No diagnoses recorded.</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Per year (all time) ── -->
          <div class="card table-card shadow-sm mb-4">
            <div class="px-4 pt-4 pb-3"><h6 class="mb-0 font-weight-bold"><i class="fe fe-calendar text-primary mr-2"></i> Use per year</h6><small class="text-muted">All years, whatever period is chosen above</small></div>
            <div class="table-responsive">
              <table class="table mb-0">
                <thead class="table-head">
                  <tr><th class="table-th px-4">Year</th><th class="table-th text-right">Times used</th><th class="table-th text-right">In encounters</th><th class="table-th text-right">In claims</th><th class="table-th text-right">Quantity</th><th class="table-th text-right">People</th><th class="table-th text-right pr-4">Claimed</th></tr>
                </thead>
                <tbody>
                  <tr v-for="y in data.by_year" :key="y.year" class="table-row">
                    <td class="px-4 py-2 font-weight-bold">{{ y.year }}</td>
                    <td class="py-2 text-right font-weight-bold">{{ num(y.uses) }}</td>
                    <td class="py-2 text-right">{{ num(y.encounter_lines) }}</td>
                    <td class="py-2 text-right">{{ num(y.claim_lines) }}</td>
                    <td class="py-2 text-right">{{ num(y.quantity) }}</td>
                    <td class="py-2 text-right">{{ num(y.patients) }}</td>
                    <td class="py-2 text-right pr-4">{{ money(y.amount_claimed) }}</td>
                  </tr>
                  <tr v-if="!data.by_year.length"><td colspan="7" class="text-center text-muted py-4">Never used yet.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import PeriodPicker from "@/views/analytics/components/PeriodPicker.vue";

/** One drug or service: how often it was used in encounters and claims, by whom and where (GET catalog/{type}/{id}/usage). */
export default {
  name: "CatalogItemUsage",
  components: { Navbar, PeriodPicker },
  data() {
    return {
      period: { from: "", to: "", granularity: "month" },
      data: null,
      loading: false,
      notFound: false,
      errorMessage: "",
      chartKey: 0,
      measure: "uses",
      measures: [{ key: "uses", label: "Times used" }, { key: "quantity", label: "Quantity" }, { key: "amount", label: "Amount claimed" }],
    };
  },
  computed: {
    isDrug() {
      return this.$route.params.type === "drug";
    },
    item() {
      return this.data && this.data.item;
    },
    kpis() {
      const k = this.data.kpis;
      return [
        { label: "Times used", value: this.num(k.uses), hint: `${this.num(k.quantity)} ${this.isDrug ? "units" : "in quantity"}` },
        { label: "People", value: this.num(k.patients), hint: k.uses_per_patient ? `${k.uses_per_patient} uses per person` : "—" },
        { label: "Encounters", value: this.num(k.encounters), hint: `${this.num(k.encounter_lines)} lines · ${this.money(k.encounter_amount)}` },
        { label: "Claims", value: this.num(k.claims), hint: `${this.money(k.amount_claimed)} claimed · ${this.money(k.amount_approved)} approved` },
        { label: "Facilities", value: this.num(k.facilities), hint: "that gave it" },
      ];
    },
    trendSeries() {
      const t = this.data.trend;
      if (this.measure === "quantity") return [{ name: "Quantity", data: t.quantity }];
      if (this.measure === "amount") return [{ name: "Amount claimed", data: t.amount_claimed }];
      return [{ name: "In encounters", data: t.encounter_lines }, { name: "In claims", data: t.claim_lines }];
    },
    trendOptions() {
      const money = this.measure === "amount";
      return {
        chart: { stacked: this.measure === "uses", toolbar: { show: true } },
        colors: this.measure === "uses" ? ["#2563eb", "#7c3aed"] : [money ? "#059669" : "#0891b2"],
        xaxis: { categories: this.data.trend.categories },
        yaxis: { labels: { formatter: (v) => (money ? this.money(v) : this.num(v)) } },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
        legend: { position: "top" },
        tooltip: { y: { formatter: (v) => (money ? this.money(v) : this.num(v)) } },
      };
    },
    genderSeries() {
      return this.data.demography.gender.map((g) => g.count);
    },
    genderOptions() {
      const keys = this.data.demography.gender.map((g) => g.key);
      return {
        labels: keys,
        colors: keys.map((k) => ({ Male: "#2563eb", Female: "#db2777" }[k] || "#94a3b8")),
        legend: { position: "bottom" },
      };
    },
    ageSeries() {
      const rows = this.data.demography.age_gender;
      const series = [{ name: "Male", data: rows.map((r) => r.male) }, { name: "Female", data: rows.map((r) => r.female) }];
      if (rows.some((r) => r.not_set)) series.push({ name: "Sex not set", data: rows.map((r) => r.not_set) });
      return series;
    },
    ageOptions() {
      return {
        chart: { stacked: true, toolbar: { show: true } },
        colors: ["#2563eb", "#db2777", "#94a3b8"],
        xaxis: { categories: this.data.demography.age_gender.map((r) => r.band) },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
        legend: { position: "top" },
      };
    },
  },
  watch: {
    "$route.fullPath"() {
      this.data = null;
      this.load();
    },
  },
  methods: {
    load() {
      if (!this.period.from) return;
      this.loading = true;
      this.notFound = false;
      this.axios
        .get(`/api/v1/auth/catalog/${this.$route.params.type}/${this.$route.params.id}/usage`, {
          params: { from: this.period.from, to: this.period.to, granularity: this.period.granularity },
        })
        .then((res) => {
          this.data = res.data.data;
          this.chartKey++;
        })
        .catch((error) => {
          const status = error.response && error.response.status;
          if (status === 404 || status === 403) {
            this.notFound = true;
            this.errorMessage = (error.response.data && error.response.data.message) || "";
          } else {
            this.$toasted.error("Could not load the usage. Please try again.", { position: "top-center", duration: 4000 });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    hbarOptions(categories) {
      return {
        chart: { toolbar: { show: false } },
        colors: ["#2563eb"],
        plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: "70%" } },
        xaxis: { categories, labels: { formatter: (v) => this.num(v) } },
      };
    },
    barHeight(rows) {
      return Math.max(200, rows.length * 28 + 60);
    },
    num(v) {
      return Number(v || 0).toLocaleString();
    },
    money(v) {
      return "₦" + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
  },
};
</script>

<style scoped>
.empty-note {
  text-align: center;
  color: #94a3b8;
  padding: 48px 0;
}
</style>
